import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './PublicProfile.css';

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { month: "short", year: "numeric" });

const CATEGORY_COLORS = {
  frontend: { bg: 'rgba(59,130,246,0.12)', color: '#60a5fa' },
  backend: { bg: 'rgba(16,185,129,0.12)', color: '#34d399' },
  fullstack: { bg: 'rgba(124,58,237,0.12)', color: '#a78bfa' },
  python: { bg: 'rgba(234,179,8,0.12)', color: '#fbbf24' },
  vlsi: { bg: 'rgba(244,63,94,0.12)', color: '#fb7185' },
  hardware: { bg: 'rgba(249,115,22,0.12)', color: '#fb923c' },
  other: { bg: 'rgba(100,116,139,0.12)', color: '#94a3b8' },
};

function CategoryChip({ category }) {
  const style = CATEGORY_COLORS[category?.toLowerCase()] || CATEGORY_COLORS.other;
  return (
    <span className="pp-chip" style={{ background: style.bg, color: style.color }}>
      {category}
    </span>
  );
}

function ReadinessBar({ score }) {
  return (
    <div className="pp-readiness">
      <div className="pp-readiness-bar">
        <div
          className="pp-readiness-fill"
          style={{ width: `${Math.min(100, score)}%` }}
        />
      </div>
      <span className="pp-readiness-val">{score}%</span>
    </div>
  );
}

export default function PublicProfile() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL || '/api'}/public-profile/u/${username}`
        );
        setProfile(res.data);
      } catch (err) {
        setError(err.response?.data?.message || "Profile not found or is private.");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [username]);

  if (loading) return (
    <div className="pp-page pp-page--loading">
      <div className="pp-spinner" />
      <p>Verifying Learnlia Profile…</p>
    </div>
  );

  if (error) return (
    <div className="pp-page pp-page--error">
      <div className="pp-error-card">
        <span className="pp-error-icon">⊘</span>
        <h2>Profile Unavailable</h2>
        <p>{error}</p>
        <a href="/" className="pp-error-btn">Return to Learnlia</a>
      </div>
    </div>
  );

  const completedTracks = profile.tracks?.filter(t => t.completed) || [];
  const activeTracks = profile.tracks?.filter(t => !t.completed) || [];

  return (
    <div className="pp-page">
      <div className="pp-container">

        {/* ── Verified badge ───────────────────────────────────── */}
        <div className="pp-verified">
          <span className="pp-verified-dot" />
          Learnlia Verified Profile
        </div>

        {/* ── Hero ─────────────────────────────────────────────── */}
        <header className="pp-hero">
          <div className="pp-avatar">
            {profile.name?.[0]?.toUpperCase()}
          </div>

          <div className="pp-hero-info">
            <h1 className="pp-name">{profile.name}</h1>

            {profile.targetRole && (
              <p className="pp-headline">
                Targeting <strong>{profile.targetRole}</strong>
                {profile.targetMonth && <> · {profile.targetMonth}</>}
              </p>
            )}

            {profile.bio && (
              <p className="pp-bio">{profile.bio}</p>
            )}

            <div className="pp-socials">
              {profile.githubUrl && (
                <a href={profile.githubUrl} target="_blank" rel="noreferrer" className="pp-social">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="pp-social-icon">
                    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                  </svg>
                  GitHub
                </a>
              )}
              {profile.linkedinUrl && (
                <a href={profile.linkedinUrl} target="_blank" rel="noreferrer" className="pp-social">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="pp-social-icon">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  LinkedIn
                </a>
              )}
              {profile.portfolioUrl && (
                <a href={profile.portfolioUrl} target="_blank" rel="noreferrer" className="pp-social">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="pp-social-icon">
                    <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                  </svg>
                  Portfolio
                </a>
              )}
            </div>
          </div>
        </header>

        {/* ── Stats row ────────────────────────────────────────── */}
        <div className="pp-stats">
          <div className="pp-stat">
            <span className="pp-stat-val">{profile.level}</span>
            <span className="pp-stat-label">Level</span>
          </div>
          <div className="pp-stat-sep" />
          <div className="pp-stat">
            <span className="pp-stat-val">{profile.streak?.current || 0}</span>
            <span className="pp-stat-label">Day Streak</span>
          </div>
          <div className="pp-stat-sep" />
          <div className="pp-stat">
            <span className="pp-stat-val">{completedTracks.length}</span>
            <span className="pp-stat-label">Tracks Done</span>
          </div>
          <div className="pp-stat-sep" />
          <div className="pp-stat">
            <span className="pp-stat-val" style={{ textTransform: 'capitalize' }}>
              {profile.primaryGoal || 'Learning'}
            </span>
            <span className="pp-stat-label">Goal</span>
          </div>
        </div>

        {/* ── Completed tracks ─────────────────────────────────── */}
        {completedTracks.length > 0 && (
          <section className="pp-section">
            <h2 className="pp-section-title">
              <span className="pp-section-icon">🏆</span>
              Completed Tracks
            </h2>
            <div className="pp-tracks">
              {completedTracks.map((t) => (
                <div key={t.trackSlug} className="pp-track-card pp-track-card--done">
                  <div className="pp-track-left">
                    <div className="pp-track-name-row">
                      <a
                        href={`/tracks/${t.trackSlug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="pp-track-name"
                      >
                        {t.trackTitle}
                      </a>
                      <span className="pp-done-badge">✓ Completed</span>
                    </div>
                    <div className="pp-track-meta">
                      <CategoryChip category={t.category} />
                      {t.goal && <span className="pp-track-goal">{t.goal}</span>}
                    </div>
                  </div>
                  <div className="pp-track-right">
                    <span className="pp-readiness-label">Readiness</span>
                    <ReadinessBar score={t.readinessScore} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Active tracks ────────────────────────────────────── */}
        {activeTracks.length > 0 && (
          <section className="pp-section">
            <h2 className="pp-section-title">
              <span className="pp-section-icon">⚡</span>
              In Progress
            </h2>
            <div className="pp-tracks">
              {activeTracks.map((t) => (
                <div key={t.trackSlug} className="pp-track-card">
                  <div className="pp-track-left">
                    <div className="pp-track-name-row">
                      <a
                        href={`/tracks/${t.trackSlug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="pp-track-name"
                      >
                        {t.trackTitle}
                      </a>
                    </div>
                    <div className="pp-track-meta">
                      <CategoryChip category={t.category} />
                      {t.goal && <span className="pp-track-goal">{t.goal}</span>}
                      <span className="pp-track-phase">Phase {t.currentPhase}</span>
                    </div>
                  </div>
                  <div className="pp-track-right">
                    <span className="pp-readiness-label">Readiness</span>
                    <ReadinessBar score={t.readinessScore} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Certificates ─────────────────────────────────────── */}
        {profile.certificates?.length > 0 && (
          <section className="pp-section">
            <h2 className="pp-section-title">
              <span className="pp-section-icon">📜</span>
              Certificates
            </h2>
            <div className="pp-certs">
              {profile.certificates.map((c) => (
                <a
                  key={c.certificateId}
                  href={`/certificate/${c.certificateId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="pp-cert-card"
                >
                  <div className="pp-cert-icon">🏆</div>
                  <div className="pp-cert-info">
                    <strong>{c.trackName}</strong>
                    <span>Issued {fmtDate(c.issuedAt)}</span>
                  </div>
                  <span className="pp-cert-arrow">↗</span>
                </a>
              ))}
            </div>
          </section>
        )}

        {/* ── Footer ───────────────────────────────────────────── */}
        <footer className="pp-footer">
          <div className="pp-footer-brand">
            <span className="pp-footer-dot" />
            Learnlia
          </div>
          <p>
            Proof backed by real video-level completion tracking.
            <a href="/" className="pp-footer-link">learnlia.com</a>
          </p>
        </footer>

      </div>
    </div>
  );
}